import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useConfigStore } from '../../stores/configStore';
import { useLocationStore, useMapConsentRequired } from '../../stores/locationStore';
import { updateUser } from '../../services/userService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import { colors } from '../../styles/colors';
import { PinSettingsModal } from '../../components/PinLock';
import { isNative, isWeb } from '../../lib/platform';
import type { NewUserDefaults } from '../../types/config';

/* ─── Reusable Components ─── */

function SectionTitle({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <p className="mb-2 mt-5 px-1 text-xs font-bold uppercase tracking-wider"
      style={{ color: color ?? colors.primary }}>
      {children}
    </p>
  );
}

function Row({ label, desc, children }: { label: string; desc?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center justify-between rounded-lg px-4 py-3"
      style={{ backgroundColor: colors.card }}>
      <div className="mr-3 flex-1">
        <p className="text-sm font-semibold" style={{ color: colors.text }}>{label}</p>
        {desc && <p className="mt-0.5 text-[11px]" style={{ color: colors.textSecondary }}>{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      className="relative h-6 w-11 shrink-0 rounded-full transition-colors"
      style={{ backgroundColor: checked && !disabled ? colors.primary : '#555', opacity: disabled ? 0.4 : 1 }}>
      <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform"
        style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  );
}

function OptionButtons({ options, value, onChange }: {
  options: { key: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)}
          className="rounded px-2.5 py-1 text-[11px] font-semibold transition-colors"
          style={{
            backgroundColor: value === o.key ? colors.primary : colors.surface,
            color: value === o.key ? '#fff' : colors.textSecondary,
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Session-scoped "Show my location on map" toggle (Apple 5.1.2 compliance).
 * Reads/writes shareLocationOnMap in locationStore. Resets on logout.
 */
function ShareLocationRow() {
  const { i18n } = useTranslation();
  const share = useLocationStore((s) => s.shareLocationOnMap);
  const dontAsk = useLocationStore((s) => s.dontAskMapConsent);
  const user = useAuthStore((s) => s.user);
  const consentRequired = useMapConsentRequired();
  const isKo = i18n.language === 'ko';

  // On platforms where admin has disabled the consent flow, the user is always visible
  // on the map — no toggle is exposed (nor is the "ask on login" toggle).
  if (!consentRequired) return null;

  return (
    <>
      <Row label={isKo ? '지도에 내 위치 표시 (이번 접속)' : 'Show my location on map (this session)'}
        desc={isKo
          ? '켜면 다른 사용자의 지도/목록에 내 아바타가 표시됩니다. 로그아웃 시 자동 "숨김"으로 초기화됩니다.'
          : 'When on, your avatar appears on other users\' maps/lists. Resets to "off" on logout.'}>
        <Toggle checked={share} onChange={async (v) => {
          const store = useLocationStore.getState();
          store.setShareLocationOnMap(v);
          store.setShareLocationPrompted(true);
          if (!v && user) {
            const { clearMyLocationOnMap } = await import('../../services/locationService');
            clearMyLocationOnMap(user.uid).catch(() => {});
          }
        }} />
      </Row>
      <Row label={isKo ? '로그인 시 지도 표시 안내 팝업' : 'Show map-visibility prompt on login'}
        desc={isKo
          ? '끄면 로그인마다 나오는 "지도에 표시하시겠습니까?" 팝업이 숨겨집니다. 기본값은 항상 "숨김" 이며, 이 화면에서 언제든 수동으로 켤 수 있습니다.'
          : 'If off, the "Show on Map?" prompt on each login is hidden. Your default remains "Hidden" — you can still turn sharing on from this screen anytime.'}>
        <Toggle checked={!dontAsk} onChange={(v) => {
          useLocationStore.getState().setDontAskMapConsent(!v);
        }} />
      </Row>
    </>
  );
}


/* ─── Main ─── */

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const { theme, setTheme } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const config = useConfigStore((s) => s.config);

  // Local setting states (synced with user doc)
  const [pushEnabled, setPushEnabled] = useState(() => {
    // Per-device: check localStorage first, fallback to user doc
    const local = user ? localStorage.getItem(`push_enabled_${user.uid}`) : null;
    if (local !== null) return local === 'true';
    return user?.pushNotificationsEnabled ?? true;
  });
  const [serverNotif, setServerNotif] = useState(user?.pushSettings?.serverNotifications ?? true);
  const [msgNotif, setMsgNotif] = useState(user?.pushSettings?.messageNotifications !== false);
  const [nearbyNotif, setNearbyNotif] = useState(user?.pushSettings?.nearbyNotifications ?? true);
  const [dmNotif, setDmNotif] = useState(user?.pushSettings?.dmNotifications ?? true);
  const [whenClosedOnly, setWhenClosedOnly] = useState(user?.pushSettings?.whenClosedOnly ?? false);
  const [genderFilter, setGenderFilter] = useState(user?.genderFilter ?? 'all');
  const [rejectBot, setRejectBot] = useState(user?.rejectBotMessages ?? false);
  const [activeThreshold, setActiveThreshold] = useState(user?.activeThresholdMinutes ?? 60);
  const pinEnabled = !!(user?.pinEnabled && user?.pinHash);
  const [pinModal, setPinModal] = useState<'setup' | 'disable' | null>(null);
  const [jumpRangeMin, setJumpRangeMin] = useState(user?.locationJumpRange?.min ?? 100);
  const [jumpRangeMax, setJumpRangeMax] = useState(user?.locationJumpRange?.max ?? 150);
  const [connNotifEnabled, setConnNotifEnabled] = useState(user?.connectionNotificationEnabled ?? true);
  const [connNotifDistance, setConnNotifDistance] = useState(user?.connectionNotificationDistanceThreshold ?? 1000);
  const syncIntervalSec = useLocationStore((s) => s.syncIntervalSec);
  const radarRadius = useLocationStore((s) => s.radarRadius);
  const messageReceptionRadius = useLocationStore((s) => s.messageReceptionRadius);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [pushDeniedModal, setPushDeniedModal] = useState(false);

  // Persist setting changes to Firestore
  const saveUserSetting = async (data: Record<string, unknown>) => {
    if (!user) return;
    try { await updateUser(user.uid, data as any); } catch (e) { console.error(e); }
  };

  const handlePushToggle = async (v: boolean) => {
    if (v) {
      // Turning on: check/request permission first
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive !== 'granted') {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== 'granted') {
          setPushDeniedModal(true);
          return;
        }
      } catch { /* web or plugin not available */ }
    }
    setPushEnabled(v);
    // Save per-device using localStorage (device+account specific)
    if (user) localStorage.setItem(`push_enabled_${user.uid}`, String(v));
    if (v) {
      saveUserSetting({ pushNotificationsEnabled: true });
      // Re-register push token for this device
      import('../../services/pushService').then(({ initPushNotifications, savePushToken }) => {
        initPushNotifications(user!.uid, (token) => savePushToken(user!.uid, token));
      }).catch(() => {});
    } else {
      // Turning off: clear push token + 하위 알림 설정도 모두 끄기
      setServerNotif(false);
      setMsgNotif(false);
      setNearbyNotif(false);
      setDmNotif(false);
      setWhenClosedOnly(false);
      saveUserSetting({
        pushNotificationsEnabled: false,
        pushToken: '',
        'pushSettings.serverNotifications': false,
        'pushSettings.messageNotifications': false,
        'pushSettings.nearbyNotifications': false,
        'pushSettings.dmNotifications': false,
        'pushSettings.whenClosedOnly': false,
      });
    }
  };
  const handleServerNotif = (v: boolean) => {
    setServerNotif(v);
    saveUserSetting({ 'pushSettings.serverNotifications': v });
  };
  const handleMsgNotif = (v: boolean) => {
    setMsgNotif(v);
    saveUserSetting({ 'pushSettings.messageNotifications': v });
  };
  const handleNearbyNotif = (v: boolean) => {
    setNearbyNotif(v);
    saveUserSetting({ 'pushSettings.nearbyNotifications': v });
  };
  const handleDmNotif = (v: boolean) => {
    setDmNotif(v);
    saveUserSetting({ 'pushSettings.dmNotifications': v });
  };
  const handleWhenClosed = (v: boolean) => {
    setWhenClosedOnly(v);
    saveUserSetting({ 'pushSettings.whenClosedOnly': v });
  };
  const handleGenderFilter = (v: string) => {
    setGenderFilter(v as any);
    saveUserSetting({ genderFilter: v });
  };
  const handleRejectBot = (v: boolean) => {
    setRejectBot(v);
    saveUserSetting({ rejectBotMessages: v });
  };


  return (
    <div className="flex h-full flex-col overflow-y-auto" style={{ backgroundColor: colors.background }}>
      <div className="p-3 pb-8">

        {/* ── App Info ── */}
        <SectionTitle>{t('settings.appInfo')}</SectionTitle>
        <Row label={t('settings.appVersion')} desc={`v${__APP_VERSION__}`}>
          <button onClick={async () => {
            const { getLatestReleaseNote } = await import('../../services/boardService');
            const rn = await getLatestReleaseNote(i18n.language === 'ko' ? 'ko' : 'en');
            if (rn) window.dispatchEvent(new CustomEvent('open-info-post', { detail: { post: rn } }));
            else alert(i18n.language === 'ko' ? '릴리즈 노트가 없습니다.' : 'No release notes.');
          }}
            className="rounded px-2 py-1 text-[10px] font-bold" style={{ backgroundColor: colors.card, color: colors.primary, border: `1px solid ${colors.primary}40` }}>
            {i18n.language === 'ko' ? '릴리즈 노트' : 'Release Notes'}
          </button>
        </Row>
        <Row label={i18n.language === 'ko' ? '앱 공유' : 'Share App'}>
          <div className="flex gap-1">
            {([
              { key: 'web' as const, icon: '🌐', label: 'Web', color: '#888888' },
              ...(!(isNative && typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.getPlatform?.() === 'ios') ? [{ key: 'android' as const, icon: '🤖', label: 'Android', color: '#3ddc84' }] : []),
              { key: 'ios' as const, icon: '🍎', label: 'iOS', color: '#007aff' },
            ]).map((p) => (
              <button key={p.key} onClick={async () => {
                const { shareAppForPlatform } = await import('../../utils/share');
                await shareAppForPlatform(p.key, i18n.language);
              }}
                className="rounded px-2 py-1 text-[10px] font-bold"
                style={{ backgroundColor: p.color + '20', color: p.color, border: `1px solid ${p.color}60` }}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </Row>

        {/* ── Notifications ── */}
        <SectionTitle>{i18n.language === 'ko' ? '알림' : 'Notifications'}</SectionTitle>
        {/* Web: browser notifications (admin sees all) */}
        {(isWeb || user?.isAdmin) && 'Notification' in window && (
          <Row label={i18n.language === 'ko' ? '브라우저 알림' : 'Browser Notifications'}
            desc={Notification.permission === 'granted' ? (i18n.language === 'ko' ? '허용됨' : 'Allowed') : (i18n.language === 'ko' ? '탭이 비활성일 때 알림' : 'Notify when tab is inactive')}>
            <button onClick={() => Notification.requestPermission()}
              className="rounded px-2 py-1 text-xs font-bold"
              style={{ backgroundColor: Notification.permission === 'granted' ? `${colors.primary}20` : colors.card, color: Notification.permission === 'granted' ? colors.primary : colors.textMuted, border: `1px solid ${colors.border}` }}>
              {Notification.permission === 'granted' ? '✓' : (i18n.language === 'ko' ? '허용' : 'Allow')}
            </button>
          </Row>
        )}
        {/* Mobile: push notifications (admin sees all) */}
        {(isNative || user?.isAdmin) && (
          <>
            <Row label={i18n.language === 'ko' ? '푸시 알림' : 'Push Notifications'}
              desc={i18n.language === 'ko' ? '모바일 푸시 알림 수신' : 'Receive mobile push notifications'}>
              <Toggle checked={pushEnabled} onChange={handlePushToggle} />
            </Row>
            <Row label={i18n.language === 'ko' ? '서버 알림' : 'Server Notifications'}
              desc={i18n.language === 'ko' ? '서버에서 보내는 공지/알림' : 'Announcements from server'}>
              <Toggle checked={serverNotif} onChange={handleServerNotif} disabled={!pushEnabled} />
            </Row>
            <Row label={i18n.language === 'ko' ? '백그라운드에서만 알림' : 'Notify only when closed'}
              desc={i18n.language === 'ko' ? '앱이 닫혀있을 때만 푸시 알림' : 'Push only when app is in background'}>
              <Toggle checked={whenClosedOnly} onChange={handleWhenClosed} disabled={!pushEnabled} />
            </Row>
          </>
        )}
        {/* Common notification settings */}
        <Row label={i18n.language === 'ko' ? '메시지 알림' : 'Message Notifications'}
          desc={i18n.language === 'ko' ? '새 메시지 수신 시 알림' : 'Notify on new messages'}>
          <Toggle checked={msgNotif} onChange={handleMsgNotif} disabled={!pushEnabled} />
        </Row>
        <Row label={t('settings.nearbyNotifications')}
          desc={i18n.language === 'ko' ? '주변 사용자 접속 알림' : 'Nearby user notifications'}>
          <Toggle checked={nearbyNotif} onChange={handleNearbyNotif} disabled={!pushEnabled} />
        </Row>
        <Row label={t('settings.dmNotifications')}
          desc={i18n.language === 'ko' ? '쪽지 수신 알림' : 'DM notifications'}>
          <Toggle checked={dmNotif} onChange={handleDmNotif} disabled={!pushEnabled} />
        </Row>

        {/* ── Email Notifications ── */}
        {user && !user.isGuest && (
          <>
            <SectionTitle>{t('emailNotifications.title')}</SectionTitle>
            <Row label={t('emailNotifications.onComment')}
              desc={t('emailNotifications.onCommentDesc')}>
              <Toggle checked={user.emailNotifications?.onComment ?? true}
                onChange={(v) => saveUserSetting({ 'emailNotifications.onComment': v })} />
            </Row>
            <Row label={t('emailNotifications.onReportReply')}
              desc={t('emailNotifications.onReportReplyDesc')}>
              <Toggle checked={user.emailNotifications?.onReportReply ?? true}
                onChange={(v) => saveUserSetting({ 'emailNotifications.onReportReply': v })} />
            </Row>
            <Row label={t('emailNotifications.onDmReceived')}
              desc={t('emailNotifications.onDmReceivedDesc')}>
              <Toggle checked={user.emailNotifications?.onDmReceived ?? true}
                onChange={(v) => saveUserSetting({ 'emailNotifications.onDmReceived': v })} />
            </Row>
          </>
        )}

        {/* ── Message & AI ── */}
        <SectionTitle>메시지 설정</SectionTitle>
        <Row label={t('settings.genderFilter')}>
          <OptionButtons
            options={[
              { key: 'all', label: t('settings.all') },
              { key: 'male', label: t('auth.male') },
              { key: 'female', label: t('auth.female') },
            ]}
            value={genderFilter}
            onChange={handleGenderFilter}
          />
        </Row>
        <Row label={isKo ? 'AI Bot 표시' : 'Show AI bots'}
          desc={isKo
            ? '끄면 AI 봇의 메시지, 지도 아이콘, 주변/최근 사용자 목록이 모두 숨겨집니다.'
            : 'When off, AI bot messages, map markers and nearby/recent lists are all hidden.'}>
          <Toggle checked={!rejectBot} onChange={(v) => handleRejectBot(!v)} />
        </Row>

        {/* ── Radar / Map ── */}
        <SectionTitle>{i18n.language === 'ko' ? '레이더 / 지도' : 'Radar / Map'}</SectionTitle>
        <ShareLocationRow />
        <Row label={i18n.language === 'ko' ? '사용자 표시 기준' : 'User visibility'}
          desc={i18n.language === 'ko' ? `마지막 활동 ${activeThreshold}분 이내 사용자만 표시` : `Show users active within ${activeThreshold} min`}>
          <select
            value={activeThreshold}
            onChange={(e) => {
              const v = Number(e.target.value);
              setActiveThreshold(v);
              saveUserSetting({ activeThresholdMinutes: v });
            }}
            className="rounded px-2 py-1 text-sm"
            style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
            {[15, 30, 60, 120, 360, 720, 1440].map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m}${i18n.language === 'ko' ? '분' : 'min'}` : `${m / 60}${i18n.language === 'ko' ? '시간' : 'h'}`}
              </option>
            ))}
          </select>
        </Row>

        <Row label={i18n.language === 'ko' ? '위치 갱신 주기' : 'Location sync interval'}
          desc={i18n.language === 'ko' ? `${syncIntervalSec}초마다 위치(주파수)를 갱신합니다` : `Update location every ${syncIntervalSec}s`}>
          <select value={syncIntervalSec}
            onChange={(e) => { const v = Number(e.target.value); useLocationStore.getState().setSyncInterval(v); saveUserSetting({ syncIntervalSec: v }); }}
            className="rounded px-2 py-1 text-sm"
            style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
            {[1, 3, 5, 10, 15, 20, 30, 45, 60].map((s) => (
              <option key={s} value={s}>{s}{i18n.language === 'ko' ? '초' : 's'}</option>
            ))}
          </select>
        </Row>

        {/* ── Location Jump ── */}
        <SectionTitle>{i18n.language === 'ko' ? '위치 점프' : 'Location Jump'}</SectionTitle>
        <Row label={i18n.language === 'ko' ? '점프 범위 (최소~최대)' : 'Jump Range (min~max)'}
          desc={i18n.language === 'ko' ? '위치 점프 시 이동할 거리 범위. 0-0 설정 시 정확한 위치로 점프.' : 'Distance range for location jump. Set 0-0 for exact location.'}>
          <div className="flex items-center gap-1">
            <input type="number" value={jumpRangeMin} min={0} max={1000} step={10}
              onChange={(e) => { const v = Number(e.target.value); setJumpRangeMin(v); saveUserSetting({ locationJumpRange: { min: v, max: jumpRangeMax } }); }}
              className="w-16 rounded border px-1 py-0.5 text-center text-xs" style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
            <span className="text-xs" style={{ color: colors.textMuted }}>~</span>
            <input type="number" value={jumpRangeMax} min={0} max={1000} step={10}
              onChange={(e) => { const v = Number(e.target.value); setJumpRangeMax(v); saveUserSetting({ locationJumpRange: { min: jumpRangeMin, max: v } }); }}
              className="w-16 rounded border px-1 py-0.5 text-center text-xs" style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
            <span className="text-xs" style={{ color: colors.textMuted }}>m</span>
          </div>
        </Row>

        {/* ── Connection Notification ── */}
        <SectionTitle>{i18n.language === 'ko' ? '접속 알림' : 'Connection Notification'}</SectionTitle>
        <Row label={i18n.language === 'ko' ? '접속 알림 발송' : 'Send notification'}
          desc={i18n.language === 'ko' ? '접속 시 주변 사용자에게 알림 메시지를 보냅니다' : 'Notify nearby users when you connect'}>
          <Toggle checked={connNotifEnabled} onChange={(v) => { setConnNotifEnabled(v); saveUserSetting({ connectionNotificationEnabled: v }); }} />
        </Row>
        {connNotifEnabled && (
          <Row label={i18n.language === 'ko' ? '위치 변경 기준 거리' : 'Distance threshold'}
            desc={i18n.language === 'ko' ? '이 거리 이상 이동 시 접속 알림을 다시 발송합니다' : 'Re-send notification after moving this distance'}>
            <select value={connNotifDistance}
              onChange={(e) => { const v = Number(e.target.value); setConnNotifDistance(v); saveUserSetting({ connectionNotificationDistanceThreshold: v }); }}
              className="rounded px-2 py-1 text-sm" style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
              {[500, 1000, 2000, 5000, 10000, 50000].map(m => (
                <option key={m} value={m}>{m < 1000 ? `${m}m` : `${m/1000}km`}</option>
              ))}
            </select>
          </Row>
        )}

        {/* ── Message History ── */}
        <SectionTitle>{i18n.language === 'ko' ? '메시지 수신 제한' : 'Message History'}</SectionTitle>
        <Row label={i18n.language === 'ko' ? '재접속 시 불러올 기간' : 'History on reconnect'}
          desc={i18n.language === 'ko' ? '부재 중 수신된 메시지를 불러올 최대 기간 (셀 소모)' : 'Max period to load missed messages (costs cells)'}>
          <select
            value={user?.messageHistoryHours ?? 6}
            onChange={(e) => saveUserSetting({ messageHistoryHours: Number(e.target.value) })}
            className="rounded px-2 py-1 text-sm"
            style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
            {[0, 1, 3, 6, 12, 24].map((h) => (
              <option key={h} value={h}>
                {h === 0 ? (i18n.language === 'ko' ? '불러오지 않음' : 'None') : `${h}${i18n.language === 'ko' ? '시간' : 'h'}`}
              </option>
            ))}
          </select>
        </Row>

        {/* ── Security ── */}
        <SectionTitle>{i18n.language === 'ko' ? '보안' : 'Security'}</SectionTitle>
        <Row label="PIN" desc={i18n.language === 'ko' ? '앱 시작 시 PIN 잠금' : 'Lock app with PIN on start'}>
          <Toggle checked={pinEnabled}
            onChange={(v) => setPinModal(v ? 'setup' : 'disable')} />
        </Row>
        {pinEnabled && (
          <Row label={i18n.language === 'ko' ? 'PIN 유예 시간' : 'PIN Grace Period'}
            desc={i18n.language === 'ko' ? '이 시간 내 복귀 시 PIN을 묻지 않음' : 'Skip PIN if returning within this time'}>
            <div className="flex items-center gap-1">
              <input type="number" min={0} max={3600} step={5}
                value={user?.pinGracePeriodSec ?? 10}
                onChange={(e) => saveUserSetting({ pinGracePeriodSec: Math.max(0, Math.min(3600, Number(e.target.value))) })}
                className="w-16 rounded border px-1 py-0.5 text-center text-xs"
                style={{ backgroundColor: colors.surface, color: colors.text, borderColor: colors.border }} />
              <span className="text-xs" style={{ color: colors.textMuted }}>{i18n.language === 'ko' ? '초' : 'sec'}</span>
            </div>
          </Row>
        )}

        {/* ── Data ── */}
        <SectionTitle>{i18n.language === 'ko' ? '데이터 관리' : 'Data'}</SectionTitle>
        <button className="mb-1.5 w-full rounded-lg px-4 py-3 text-left text-sm font-semibold"
          style={{ backgroundColor: colors.card, color: colors.textSecondary }}
          onClick={() => {
            if (confirm(i18n.language === 'ko' ? '채팅 기록을 삭제하시겠습니까?\n삭제 후 이전 메시지는 더 이상 표시되지 않습니다.' : 'Clear chat history?\nPrevious messages will no longer be shown.')) {
              // 모든 채팅 관련 캐시 삭제
              localStorage.removeItem('ourhertz_msg_cache');
              localStorage.removeItem('ourhertz_blur_map');
              if (user?.uid) localStorage.removeItem(`ourhertz_processed_msgs_${user.uid}`);
              // 이 시점 이전의 메시지는 서버에서 받아도 표시하지 않음
              localStorage.setItem('ourhertz_chat_cleared_at', String(Date.now()));
              alert(i18n.language === 'ko' ? '채팅 기록이 초기화되었습니다.' : 'Chat history cleared.');
              window.location.reload();
            }
          }}>
          {i18n.language === 'ko' ? '채팅 기록 초기화' : 'Clear Chat History'}
        </button>
        <button className="mb-1.5 w-full rounded-lg px-4 py-3 text-left text-sm font-semibold"
          style={{ backgroundColor: colors.card, color: colors.textSecondary }}
          onClick={() => {
            import('../../components/NoticePopup').then(({ clearDismissedNotices }) => {
              clearDismissedNotices();
              alert(i18n.language === 'ko' ? '공지사항이 다시 표시됩니다.' : 'Notices will show again.');
            });
          }}>
          {i18n.language === 'ko' ? '공지사항 다시 보기 초기화' : 'Reset dismissed notices'}
        </button>

        {/* ── Admin ── */}
        {user?.isAdmin && (
          <>
            <SectionTitle color={colors.warning}>{t('admin.title')}</SectionTitle>
            <Link to="/admin"
              className="mb-1.5 block rounded-lg px-4 py-3 text-sm font-bold"
              style={{ backgroundColor: colors.card, color: colors.warning, borderLeft: `3px solid ${colors.warning}` }}>
              {t('admin.title')} →
            </Link>
            <button
              disabled={savingTemplate}
              onClick={async () => {
                const msg = i18n.language === 'ko'
                  ? '현재 설정을 신규 가입자 기본값 템플릿으로 저장하시겠습니까?'
                  : 'Save current settings as new user defaults template?';
                if (!confirm(msg)) return;
                setSavingTemplate(true);
                try {
                  const template: NewUserDefaults = {
                    radius: user?.radius ?? 5,
                    rejectBotMessages: rejectBot,
                    pushNotificationsEnabled: pushEnabled,
                    pushSettings: {
                      serverNotifications: serverNotif,
                      messageNotifications: msgNotif,
                      nearbyNotifications: nearbyNotif,
                      dmNotifications: dmNotif,
                      whenClosedOnly: whenClosedOnly,
                    },
                    genderFilter,
                    activeThresholdMinutes: activeThreshold,
                    syncIntervalSec,
                    locationJumpRange: { min: jumpRangeMin, max: jumpRangeMax },
                    connectionNotificationEnabled: connNotifEnabled,
                    connectionNotificationDistanceThreshold: connNotifDistance,
                    messageHistoryHours: user?.messageHistoryHours ?? 6,
                    radarRadius,
                    receptionMode: (config.messageReception?.defaultMode as 'blur_beyond' | 'sensitivity_only') ?? 'blur_beyond',
                    messageReceptionRadius,
                  };
                  await updateDoc(doc(db, 'appConfig', 'main'), { newUserDefaults: template });
                  alert(i18n.language === 'ko' ? '신규 가입자 기본값이 저장되었습니다.' : 'New user defaults template saved.');
                } catch (e) {
                  console.error(e);
                  alert(i18n.language === 'ko' ? '저장에 실패했습니다.' : 'Failed to save.');
                } finally {
                  setSavingTemplate(false);
                }
              }}
              className="mb-1.5 w-full rounded-lg px-4 py-3 text-left text-sm font-bold"
              style={{ backgroundColor: colors.card, color: colors.warning, borderLeft: `3px solid ${colors.warning}`, opacity: savingTemplate ? 0.5 : 1 }}>
              {savingTemplate
                ? (i18n.language === 'ko' ? '저장 중...' : 'Saving...')
                : (i18n.language === 'ko' ? '현재 설정을 신규 가입자 기본값으로 저장' : 'Save as new user defaults template')}
            </button>
          </>
        )}

        {/* ── Logout ── */}
        <div className="mt-6">
          <button onClick={() => {
            const msg = i18n.language === 'ko' ? '로그아웃 하시겠습니까?' : 'Are you sure you want to log out?';
            if (confirm(msg)) logout();
          }}
            className="w-full rounded-lg py-3 text-center text-sm font-bold"
            style={{ backgroundColor: `${colors.error}18`, color: colors.error }}>
            {t('auth.logout')}
          </button>
        </div>

        {/* ── Support ── */}
        <SectionTitle>{i18n.language === 'ko' ? '고객 지원' : 'Support'}</SectionTitle>
        <Row label={i18n.language === 'ko' ? '문의 이메일' : 'Contact Email'}
          desc="idisrock@gmail.com">
          <button onClick={() => {
            window.open('mailto:idisrock@gmail.com', '_blank');
          }}
            className="rounded px-2 py-1 text-[10px] font-bold"
            style={{ backgroundColor: `${colors.primary}20`, color: colors.primary, border: `1px solid ${colors.primary}40` }}>
            {i18n.language === 'ko' ? '메일 보내기' : 'Send Email'}
          </button>
        </Row>
        <div className="mb-1.5 rounded-lg px-4 py-3" style={{ backgroundColor: colors.card }}>
          <p className="text-[11px] leading-relaxed" style={{ color: colors.textMuted }}>
            {i18n.language === 'ko'
              ? '부적절한 콘텐츠 또는 악용 사례를 발견하시면 위 이메일로 신고해 주세요. 24시간 이내에 조치합니다.'
              : 'If you encounter objectionable content or abuse, please report it via the email above. We will take action within 24 hours.'}
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6 pb-2 text-center">
          <p className="text-[10px]" style={{ color: colors.textMuted }}>
            {isNative ? `App v${__APP_VERSION__}` : `Web v${__APP_VERSION__}`}
          </p>
          <p className="text-[10px]" style={{ color: colors.textMuted }}>
            &copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{i18n.language === 'ko' ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.
          </p>
        </div>
      </div>

      {/* PIN Settings Modal */}
      {pinModal && (
        <PinSettingsModal mode={pinModal} pinHash={user?.pinHash} onDone={() => {
          setPinModal(null);
        }} />
      )}

      {/* Push permission denied modal */}
      {pushDeniedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPushDeniedModal(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 w-full max-w-sm rounded-2xl p-5"
            style={{ backgroundColor: colors.surface, border: `1.5px solid ${colors.border}` }}
            onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 text-center text-sm font-bold" style={{ color: colors.text }}>
              {i18n.language === 'ko' ? '푸시 알림 권한 필요' : 'Push Permission Required'}
            </h3>
            <p className="mb-4 whitespace-pre-line text-center text-xs" style={{ color: colors.textMuted }}>
              {(() => {
                const isIOS = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.getPlatform?.() === 'ios';
                if (isIOS) {
                  return i18n.language === 'ko'
                    ? '푸시 알림 권한이 꺼져 있습니다.\n\n설정 → OurHertz(우리들의 주파수) → 알림에서\n알림 허용을 켜주세요.'
                    : 'Push notification permission is off.\n\nGo to Settings → OurHertz → Notifications\nand enable Allow Notifications.';
                }
                return i18n.language === 'ko'
                  ? '푸시 알림 권한이 꺼져 있습니다.\n\n설정 → 앱 → OurHertz → 알림에서\n알림을 허용해주세요.'
                  : 'Push notification permission is off.\n\nGo to Settings → Apps → OurHertz → Notifications\nand enable notifications.';
              })()}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setPushDeniedModal(false)}
                className="flex-1 rounded-lg py-2.5 text-center text-xs font-bold"
                style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
                {i18n.language === 'ko' ? '닫기' : 'Close'}
              </button>
              <button onClick={async () => {
                setPushDeniedModal(false);
                try {
                  const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
                  const isIOS = typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.getPlatform?.() === 'ios';
                  if (isIOS) {
                    await NativeSettings.openIOS({ option: IOSSettings.App });
                  } else {
                    await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
                  }
                } catch {}
              }}
                className="flex-1 rounded-lg py-2.5 text-center text-xs font-bold text-white"
                style={{ backgroundColor: colors.primary }}>
                {i18n.language === 'ko' ? '설정 열기' : 'Open Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
